import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useContentIsActive } from '../../components/FeatureCard/ContentIsActiveContext';
import { Button, Menu, MenuItem, Popover, PopoverInteractionKind, Position, Checkbox } from '@blueprintjs/core';
import { emit, emitJson, emitMsg } from '../websocket/websocketSlice'
import { selectScenarioList, selectCurrentScenario, selectScenario } from './ScenarioSlice'
import './ScenarioSelectionPanel.css';

/**
 * This component communicates with the Redux store to manage the selected scenarios 
 * and the list of available scenarios. It also provides the ability to hide or show scenarios 
 * and stores this information in local storage, so that hidden scenarios persist across sessions.
 * 
 * Features:
 * - Displays a list of available scenarios in a dropdown menu.
 * - Allows users to select a scenario by clicking on it.
 * - Provides a checkbox to hide or show individual scenarios.
 * - Hidden scenarios are stored in the local storage and persist across sessions.
 * - Option to toggle visibility of hidden scenarios within the menu.
 * 
 * The component also interacts with the backend (Unity) to request the list of scenarios 
 * when the component mounts.
 * 
 * @component
 * @returns {JSX.Element} A rendered `ScenarioSelectionPanel` component with scenario selection options.
 */
const ScenarioSelectionPanel = () => {
    const dispatch = useDispatch();
    const scenarios = useSelector(selectScenarioList);
    const selectedScenario = useSelector(selectCurrentScenario);

    const [hiddenScenarios, setHiddenScenarios] = useState(() => {
        const saved = localStorage.getItem('hiddenScenarios');
        return saved ? JSON.parse(saved) : [];
    });

    const [hiddenSectionExpanded, setHiddenSectionExpanded] = useState(false);

    // Request scenarios from Unity when the component mounts
    useEffect(() => {
        requestScenarios();
    }, []);

    // Save hidden scenarios to local storage
    useEffect(() => {
        localStorage.setItem('hiddenScenarios', JSON.stringify(hiddenScenarios));
    }, [hiddenScenarios]);

    // Function to handle scenario selection
    const handleScenarioChange = (scenario) => {
        dispatch(selectScenario(scenario));
        emit({
            type: 'scenarioChange',
            scenario: scenario,
        });
        console.log("handleScenarioChange: ", scenario);
    };

    // Function to toggle a scenario's visibility
    const toggleScenarioVisibility = (scenario) => {
        setHiddenScenarios((prev) => {
            if (prev.includes(scenario)) {
                return prev.filter((s) => s !== scenario);
            } else {
                return [...prev, scenario];
            }
        });
    };

    const visibleScenarios = scenarios.filter((scenario) => !hiddenScenarios.includes(scenario));
    const hiddenScenariosList = scenarios.filter((scenario) => hiddenScenarios.includes(scenario));

    return (
        <div className="scenario-selection-panel">
            <h6>Select Scenario</h6>
            {scenarios.length > 0 ? (
                <Popover
                    interactionKind={PopoverInteractionKind.CLICK}
                    position={Position.BOTTOM_LEFT}
                    modifiers={{
                        preventOverflow: { enabled: true },
                        hide: { enabled: false },
                    }}
                    content={
                        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '0.5rem' }}
                            onClick={(e) => e.stopPropagation()}>
                            <Menu>
                                {visibleScenarios.map((scenario) => (
                                    <MenuItem
                                        key={scenario}
                                        text={
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    checked={!hiddenScenarios.includes(scenario)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleScenarioVisibility(scenario);
                                                    }}
                                                    style={{ marginRight: '0.5rem' }}
                                                />
                                                {scenario}
                                            </div>
                                        }
                                        onClick={(e) => {
                                            if (e.target.type !== 'checkbox') {
                                                handleScenarioChange(scenario);
                                            } else {
                                                e.preventDefault();
                                            }
                                        }}
                                        active={scenario === selectedScenario}
                                    />
                                ))}
                            </Menu>
                            {hiddenScenariosList.length > 0 && (
                                <div className="hidden-scenarios-section">
                                    <Button
                                        minimal
                                        text={hiddenSectionExpanded ? 'Hide Hidden Scenarios' : 'Show Hidden Scenarios'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHiddenSectionExpanded(!hiddenSectionExpanded);
                                        }}
                                    />
                                    {hiddenSectionExpanded && (
                                        <Menu style={{ marginTop: '0.5rem' }}>
                                            {hiddenScenariosList.map((scenario) => (
                                                <MenuItem
                                                    key={scenario}
                                                    text={
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Checkbox
                                                                checked={!hiddenScenarios.includes(scenario)}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleScenarioVisibility(scenario);
                                                                }}
                                                                style={{ marginRight: '0.5rem' }}
                                                            />
                                                            {scenario}
                                                        </div>
                                                    }
                                                    onClick={(e) => {
                                                        if (e.target.type !== 'checkbox') {
                                                            handleScenarioChange(scenario);
                                                        } else {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    active={scenario === selectedScenario}
                                                />
                                            ))}
                                        </Menu>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                >
                    <Button text={selectedScenario || 'Select a Scenario'} rightIcon="caret-down" />
                </Popover>
            ) : (
                <p>Loading scenarios from Unity...</p>
            )}
        </div>
    );
};

// Request scenarios from Unity
export const requestScenarios = () => {
    console.log('Requesting scenarios from Unity...');
    emitJson('requestScenarios', {});
};

export default ScenarioSelectionPanel;